import { Mutex } from 'async-mutex'
import querystringify from 'querystringify'

const errMsgNoRefreshToken = 'no refresh token'

class AuthBase {
  constructor (apiKey, http, storage, taber, forcedLogoutRoute, logger) {
    this.apiKey = apiKey
    this.h = http
    this.l = logger
    this.l.setAuth(this)
    this.lo = false
    this.lr = forcedLogoutRoute
    this.m = new Mutex()
    this.refreshedAt = null
    this.s = storage
    this.t = taber
    this.ws = new Map()
  }
  loggerFields () {
    if (!this.user) return {}
    return {
      'user_email': this.user.email,
      'user_id': this.user.id
    }
  }
  setRouter (router) {
    this.r = router
  }
  get user () {
    return this.s.user
  }
  set user (u) {
    this.s.user = u
  }
  logged () {
    return !!this.s.accessToken
  }
  async _accessToken () {
    const release = await this.m.acquire()
    try {
      return this.s.accessToken
    } finally {
      release()
    }
  }
  login (accessToken, refreshToken, user, route) {
    // User has changed
    if (!this.s.user || (user && user.id !== this.s.user.id)) {
      // Logout
      this.__logout(true, true)
    }

    // Store
    this.s.accessToken = accessToken
    if (refreshToken) this.s.refreshToken = refreshToken
    // We don't want to store rights
    if (user && user.rights) delete user.rights
    if (user) this.s.user = user

    // Redirect
    if (route) {
      // Update route
      if (this.r.history.current.query.redirect) {
        route = { path: this.r.history.current.query.redirect }
      }

      // Redirect
      this.r.push(route)
        .then(() => { this.lo = false })
        .catch(err => { this.l.error(err) })
    }
  }
  __logout (clearStorage, propagate) {
    // Prepare propagate data
    var d = {}
    if (this.s.user) d.previous_user_email = this.s.user.email

    // Clear storage
    if (clearStorage) this.s.clear()

    // Make sure to propagate after clearing storage
    if (propagate && this.t) {
      this.t.emit('logout', d)
    }

    // Close ws
    this.closeWebSockets()
  }
  logout (clearStorage, propagate, route) {
    // Already logging out
    // We do this because a race condition may occur with websocket opening asynchronously
    if (this.lo) return
    this.lo = true

    // Logout
    this.__logout(clearStorage, propagate)

    // Redirect
    if (route) {
      this.r.push(route)
        .catch(err => { this.l.error(err) })
    }
  }
  forcedLogoutRoute (redirect) {
    return {
      ...this.lr,
      query: {
        ...this.lr.query,
        redirect: redirect
      }
    }
  }
  async _refresh () {
    const release = await this.m.acquire()
    try {
      // Check refreshed at
      if (this.refreshedAt && (new Date()).getTime() - this.refreshedAt.getTime() < 60000) {
        return
      }

      // No refresh token
      if (!this.s.refreshToken) {
        var err = new Error(errMsgNoRefreshToken)
        err.noToast = true
        return Promise.reject(err)
      }

      // Refresh
      try {
        // Get data
        const d = await this._dataWithApiKey({
          method: 'get',
          url: '/v1/auth/refresh/' + this.s.refreshToken
        })

        // Send
        // Do not use _send() as it would deadlock this.m
        const resp = await this.h.send(d)

        // Store access token
        this.s.accessToken = resp.data.access_token

        // Update refreshed at
        this.refreshedAt = new Date()
        return
      } catch (err) {
        return Promise.reject(err)
      }
    } finally {
      release()
    }
  }
  async sendWithApiKey (data) {
    return this._send(data, this._dataWithApiKey.bind(this))
  }
  async _dataWithApiKey (data) {
    return {
      ...data,
      headers: {
        'X-Narative-Api-Key': this.apiKey,
        ...data.headers
      }
    }
  }
  async sendWithToken (data) {
    return this._send(data, this._dataWithToken.bind(this))
  }
  async _dataWithToken (data) {
    // Get access token
    const a = await this._accessToken()

    // Data
    return {
      ...data,
      headers: {
        'X-Narative-Access-Token': a,
        ...data.headers
      }
    }
  }
  async _send (data, dataFunc) {
    // Send
    try {
      // Get data
      const d = await dataFunc(data)

      // Send
      return await this.h.send(d)
    } catch (err) {
      // No request
      if (!err.request) return Promise.reject(err)

      // Check status code
      switch (err.request.status) {
        // Access token is invalid
        case 403:
          // Logout is required
          this.logout(true, true, this.forcedLogoutRoute(this.r.history.current.fullPath))
          break
        // Access token is expired
        case 401:
          // Refresh is required
          try {
            // Refresh
            await this._refresh()
          } catch (err) {
            // Logout is required
            if (err.message === errMsgNoRefreshToken || (err.request && err.request.status === 403)) {
              this.logout(true, true, this.forcedLogoutRoute(this.r.history.current.fullPath))
            }
            return Promise.reject(err)
          }

          // Send again
          try {
            // Get data
            const d = await dataFunc(data)

            // Send again
            return await this.h.send(d)
          } catch (err) {
            return Promise.reject(err)
          }
      }
      return Promise.reject(err)
    }
  }
  async openWebSocket (ws, filter) {
    // Ws already exists
    if (this.ws.has(ws.name)) {
      // We still need to apply filter
      if (filter) ws.filter(filter)
      return
    }

    // Store ws
    this.ws.set(ws.name, ws)

    // Get access token
    const a = await this._accessToken()

    // Open
    return ws.open(a, filter, async function (err) {
      // Retry by default
      err.retry = true

      // Check code
      switch (err.code) {
        // Access token is expired
        case 4000:
          // Refresh is required
          try {
            // Refresh
            await this._refresh()
          } catch (err) {
            // Logout is required
            if (err.message === errMsgNoRefreshToken || (err.request && err.request.status === 401)) {
              this.logout(true, true, this.forcedLogoutRoute(this.r.history.current.fullPath))
              err.retry = false
            }
            return Promise.reject(err)
          }

          // Get access token
          const a = await this._accessToken()
          return a
        // Access token is invalid
        case 4001:
          // Logout is required
          this.logout(true, true, this.forcedLogoutRoute(this.r.history.current.fullPath))

          // No retry
          err.retry = false
          break
      }
      return Promise.reject(err)
    }.bind(this))
  }
  closeWebSocket (ws) {
    ws.close()
    this.ws.delete(ws.name)
  }
  closeWebSockets () {
    this.ws.forEach((ws) => { this.closeWebSocket(ws) })
  }
  async urlWithAccessToken (u, qs) {
    if (!qs) qs = {}
    qs.access_token = await this._accessToken()
    return u + '?' + querystringify.stringify(qs)
  }
  async accessToken () {
    return this._accessToken()
  }
}

export default AuthBase
